import { line, area, curveLinear } from "d3-shape"
import { scaleLinear } from "d3-scale"
import { axisRight } from "d3-axis"
import { createSelector } from "reselect"
import { chain, find, map } from "lodash"
import { MARGINS, getHeight, getScaleX, getWidth } from "."
import { getBreakdownData } from "./data"
import { getBreakdownItems } from "./items"
import { makeDecimalPercentageFormatter } from "helpers/formatters/numbers"

export const getBreakdownMaxY = createSelector(
  [getBreakdownData, getBreakdownItems],
  (data, items) => chain(data)
    .map((datum) => chain(items)
      .map(({ id }) => datum[id])
      .max()
      .value(),
    )
    .max()
    .value(),
)

export const getBreakdownScaleY = createSelector(
  [getBreakdownMaxY, getHeight],
  (maxY, height) => {
    return scaleLinear()
      .domain([0, maxY])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

// I couldn't find a d3 function that formatted the data properly
// stack() messes up the number because it assumes we're stacking the areas (which we're not)
// group() (d3-array) couldn't get it to group the way i needed and it would be an extra dependency
// The format used is essentially the same structure as stack() but [0] will always be 0 to start from axis, not the previous area
// and [1] doesn't have the other items added to it
const getBreakdownStack = createSelector(
  [getBreakdownData, getBreakdownItems],
  (data, items) => map(items, ({ id }) => map(data, (d) =>
    ({
      0: 0,
      1: d[id],
      data: d,
    }),
  )),
)

export const getBreakdownAreas = createSelector(
  [getScaleX, getBreakdownScaleY, getBreakdownItems, getBreakdownStack],
  (scaleX, scaleY, items, stack) => {
    const f = chain(items)
      .map(({ id, area: areaColor, color, noLine }, i) => {
        const layer = stack[i]
        let lineShape = null

        if (!noLine) {
          lineShape = line()
            .x(d => scaleX(d.data.date))
            .y(d => scaleY(d[1]))
            .curve(curveLinear)
        }

        const areaShape = area()
          .x(d => scaleX(d.data.date))
          .y0(d => scaleY(d[0]))
          .y1(d => scaleY(d[1]))

        return ({
          id,
          line: lineShape && {
            d: lineShape(layer),
            style: { stroke: color, strokeWidth: 2, fill: "none" },
          },
          area: {
            d: areaShape(layer),
            style: { fill: areaColor },
          },
        })
      })
      .value()
    return f
  },
)

export const getDynamicFormatter = createSelector(
  [getBreakdownScaleY],
  (scaleY) => {
    const nbDecimals = chain(scaleY.ticks())
      .map(value => chain(value)
        .toString()
        .split(".")
        .last()
        .size()
        .value(),
      )
      .max() // Find max number of decimals
      .add(-2) // To account for the decimal format of percentages: 12.45% is not 12.45 but 0.1245

    const limitToBounds = (value) => {
      // More than 3 decimals breaks the layout
      const [min, max] = [0, 3]
      if (value < min) {
        return min
      }
      if (value > max) {
        return max
      }
      return value
    }

    return makeDecimalPercentageFormatter(limitToBounds(nbDecimals))
  },
)
export const getBreakdownYAxis = createSelector(
  [getHeight, getWidth, getBreakdownScaleY, getDynamicFormatter],
  (height, width, scaleY, formatter) => ({
    fn: axisRight()
      .scale(scaleY)
      .tickFormat(formatter),
    transform: `translate(${width - (MARGINS.right + 7)},0)`,
    labelTransform: {
      x: width - 15,
      y: height / 2,
    },
    baseline: {
      x1: MARGINS.left,
      x2: MARGINS.left,
      y1: MARGINS.top,
      y2: height - MARGINS.bottom,
    },
  }),
)

export const getFindBreakdownByOffset = createSelector(
  [getBreakdownStack, getScaleX, getBreakdownItems, getBreakdownScaleY],
  (stack, scaleX, items, scaleY) => offset => {
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return map(items, (item, i) => {
      if (!item.noLine) {
        const data = stack[i]
        const dotData = find(data, d => d.data.date >= lookup)
        if (dotData) {
          return ({
            id: item.id,
            color: item.color,
            y: scaleY(dotData[1]),
            x: scaleX(dotData.data.date),
          })
        }
      }

      return null
    })
      .filter(item => item)
  },
)
